/**
 * TypeScript twin of `autopersona_core::image_src::ImageSrc` (APA-315).
 *
 * Consumed by both `src-sdk` (validates before patching `src`/`poster`/`href`
 * into the DOM on a customer page — defense in depth, since a cached SDK
 * build can be served by an older or misconfigured API; plan.md §2.3:
 * "Validierung doppelt, serverseitig beim Speichern und im SDK vor dem
 * Schreiben") and `src-frontend` (the ImageEditor's live URL validation and
 * client-side preview). The scheme allow-list and the "empty src is a valid
 * placeholder" rule must stay in lockstep with the Rust implementation in
 * `autopersona-core/src/image_src.rs`.
 *
 * Unlike `linkHref.ts`'s `isAllowedHref`, there is no "insecure but allowed"
 * scheme here — `http:` is rejected outright, not merely flagged, since
 * Mixed Content on an HTTPS customer page fails to load the image entirely.
 */
const ALLOWED_SCHEMES = ['https'];
/**
 * Extracts the scheme prefix (text before the first `:`), but only when it's
 * positioned before any `/`, `?` or `#` — i.e. an actual URI scheme, not a
 * relative path that happens to contain a colon later on (e.g. a query
 * string value).
 */
function extractScheme(raw) {
    const colon = raw.indexOf(':');
    if (colon === -1)
        return null;
    const prefix = raw.slice(0, colon);
    if (prefix === '' || /[/?#]/.test(prefix))
        return null;
    return prefix;
}
/**
 * True when `src` is empty (the placeholder "unset" state), a `#`-fragment,
 * a relative path, or uses the one allowed scheme (`https:`). False for
 * `http:`, `javascript:`, `vbscript:`, `file:`, `data:`, and any other
 * scheme not on the allow-list.
 */
export function isAllowedImageSrc(src) {
    if (src === '' || src.startsWith('#') || src.startsWith('/')) {
        return true;
    }
    const scheme = extractScheme(src);
    if (scheme === null)
        return true;
    return ALLOWED_SCHEMES.includes(scheme.toLowerCase());
}
//# sourceMappingURL=imageSrc.js.map